import React, { useContext, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, Button, Icon } from '@node-space/ui-components'
import { useWalletContext } from 'hooks/context'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useIsActiveWallet } from 'hooks/useActiveWallets'
import { useMappedRoles } from 'hooks/useMappedRoles'
import WalletModal, { WalletAction } from 'pages/Wallets/components/Modal/WalletModal'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { ActionIds } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

interface WalletButtonsCryptoProps {
  disabled?: boolean
}

export const WalletButtonsCrypto = ({ disabled }: WalletButtonsCryptoProps) => {
  const { t } = useTranslation()
  const { viewOnly } = useProfileContext()
  const { enableUsLaunchRestrictions } = useFeatureFlags()
  const [, setWalletAction] = useContext(WalletActionContext)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [walletModalAction, setWalletModalAction] = useState<WalletAction>(undefined)
  const queryClient = useQueryClient()
  const wallet = useWalletContext()
  const isSelectedWalletActive = useIsActiveWallet(wallet)
  const permissions = useMappedRoles()
  const canEdit = permissions?.wallets?.canEdit
  const canTrade = permissions?.wallets?.canTrade
  const canCreatePayOut = permissions?.payments?.canCreatePayOut
  const canEditOrTrade = canEdit || canTrade
  const hasBalance = wallet?.balance > 0

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: reactQueryKeys.wallet(wallet.id) })
  }

  const handleOpenSendSheet = () => {
    setWalletAction({
      actionId: ActionIds?.SEND,
      header: t('wallets.send.contactStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWallet: wallet,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenConvertSheet = () => {
    track.Amp.track(AmplitudeEvent.WALLET_DETAILS_CONVERT, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      currency: wallet?.currency?.code?.toLowerCase(),
      walletId: wallet?.id,
      isFiat: false,
    })
    setWalletAction({
      actionId: ActionIds?.CONVERT,
      header: t('wallets.convert.detailsStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWallet: wallet,
      },
    })
    setIsSheetOpen(true)
  }

  return (
    isSelectedWalletActive && (
      <Box flex padding={8} className="gap-3 sm:pt-0">
        {!enableUsLaunchRestrictions && (
          <Button
            testId="buttonConvert"
            disabled={disabled}
            onClick={handleOpenConvertSheet}
            iconElement={<Icon name="ArrowsLeftRight" />}
          >
            {t('convert')}
          </Button>
        )}

        {canEditOrTrade && canCreatePayOut && isSelectedWalletActive && hasBalance && (
          <Button
            testId="send-crypto"
            onClick={handleOpenSendSheet}
            disabled={viewOnly}
            iconElement={<Icon name="ArrowUpRight" />}
          >
            {t('payments.sendCrypto')}
          </Button>
        )}

        <WalletModal
          action={walletModalAction}
          setWalletModalAction={setWalletModalAction}
          refresh={handleRefresh}
        />
        <WalletSheet isSheetOpen={isSheetOpen} />
      </Box>
    )
  )
}
